import { useState, useEffect, useCallback, useRef } from "react";
import { useAuthenticatedFetchWithWorkspace } from "../Keycloak/useAuthenticatedFetchWithWorkspace";

interface Options<TApi, TResult> {
    endpoint: string;
    transform?: (items: TApi[]) => TResult[];
}

const useFetchList = <TApi, TResult = TApi>({ endpoint, transform }: Options<TApi, TResult>) => {
    const [data, setData] = useState<TResult[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const { authFetch, isReady, isLoading: workspaceLoading, workspaceId } = useAuthenticatedFetchWithWorkspace();
    const transformRef = useRef(transform);

    useEffect(() => {
        transformRef.current = transform;
    }, [transform]);

    const doFetch = useCallback(async (signal?: AbortSignal): Promise<void> => {
        try {
            setLoading(true);
            setError("");
            const result: TApi[] = await authFetch(endpoint, { method: "GET", signal });
            if (signal?.aborted) return;
            if (!result) throw new Error("Failed to retrieve data.");
            const currentTransform = transformRef.current;
            setData(currentTransform ? currentTransform(result) : (result as unknown as TResult[]));
        } catch (err: unknown) {
            if (err instanceof Error && err.name === "AbortError") return;
            setError(err instanceof Error ? err.message : "An error occurred");
        } finally {
            if (!signal?.aborted) {
                setLoading(false);
            }
        }
    }, [authFetch, endpoint]);

    useEffect(() => {
        if (workspaceLoading) { setLoading(true); return; }
        if (!isReady) { setLoading(false); return; }
        const controller = new AbortController();
        setData(null);
        setLoading(true);
        setError("");
        doFetch(controller.signal);
        return () => controller.abort();
    }, [isReady, workspaceLoading, workspaceId, doFetch]);

    return { data, loading, error, refetch: () => doFetch() };
};

export default useFetchList;
